import React, { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { MessageSquare, FileText, Video } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { demoPosts, demoArticles, demoVideos } from '../data/demoData';
import CreatePostModal from '../components/modals/CreatePostModal';
import ContentCard from '../components/common/ContentCard';
import Sidebar from '../components/common/Sidebar';
import { useShare } from '../hooks/useShare';
import { APP_CONFIG } from '../constants/config';
import { CATEGORIES } from '../types';
import FloatingActionButton from '../components/common/FloatingActionButton';
import SEO from '../components/SEO';
// import CategoryMenu from '../components/CategoryMenu';

type ContentTab = 'posts' | 'articles' | 'videos';

interface TabInfo {
  id: ContentTab;
  label: string;
  icon: React.ReactElement;
  count: number;
}

export default function HomePage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { showToast } = useOutletContext<{ showToast: (message: string, type: 'success' | 'error' | 'info') => void }>();
  const handleShare = useShare();
  
  const [activeTab, setActiveTab] = useState<ContentTab>('posts');
  const [isCreatePostModalOpen, setIsCreatePostModalOpen] = useState(false);
  const [posts, setPosts] = useState(demoPosts);

  const tabs: TabInfo[] = [
    { id: 'posts', label: 'Posts', icon: <MessageSquare className="h-4 w-4" />, count: demoPosts.length },
    { id: 'articles', label: 'Articles', icon: <FileText className="h-4 w-4" />, count: demoArticles.length },
    { id: 'videos', label: 'Videos', icon: <Video className="h-4 w-4" />, count: demoVideos.length }
  ];

  const getCurrentContent = () => {
    switch (activeTab) {
      case 'posts':
        return posts;
      case 'articles':
        return demoArticles;
      case 'videos':
        return demoVideos;
    }
  };

  return (
    <>
      <SEO
        title="Professionals Insights & Knowledge Sharing"
        description="Join the premier platform for trends. Connect with experts, share knowledge, and stay ahead in your field."
        keywords={[
          'business insights',
          'professional networking',
          'knowledge sharing',
          'industry trends',
          'expert community',
          'business intelligence',
          'professional development'
        ]}
        type="website"
      />

      <div className="space-y-4">
        {/* <CategoryMenu /> */}

        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1 space-y-4">
            <div className="flex items-center space-x-1 overflow-x-auto pb-2 sm:pb-0 scrollbar-hide">
              {tabs.map(tab => (
                <div
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap cursor-pointer
                    ${activeTab === tab.id
                      ? theme === 'dark'
                        ? 'bg-gray-800 text-white'
                        : 'bg-white text-gray-900 shadow-sm'
                      : theme === 'dark'
                        ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white hover:shadow-sm'
                    }
                  `}
                >
                  {tab.icon}
                  <span className="text-xs">{tab.label}</span>
                  <span className="text-[10px] font-medium">
                    {tab.count}
                  </span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {getCurrentContent()?.map(content => (
                <ContentCard
                  key={content.id}
                  content={content}
                  onLike={() => {
                    showToast('Content liked!', 'success');
                  }}
                  onComment={() => {
                    navigate(`/${content.type}/${content.id}#comments`);
                  }}
                  onShare={async () => {
                    await handleShare(content);
                  }}
                />
              ))}
            </div>
          </div>

          <div className="lg:w-72">
            <div className="sticky top-20">
              <Sidebar />
            </div>
          </div>
        </div>

        <FloatingActionButton onCreatePost={() => setIsCreatePostModalOpen(true)} />

        <CreatePostModal
          isOpen={isCreatePostModalOpen}
          onClose={() => setIsCreatePostModalOpen(false)}
          onSubmit={async (postData) => {
            try {
              const defaultCategory = CATEGORIES[0];
              const newPost = {
                id: `post-${Date.now()}`,
                type: 'post' as const,
                ...postData,
                author: {
                  id: 'current-user',
                  username: 'TechMaster486',
                  avatar: APP_CONFIG.defaultAvatarUrl,
                  role: 'user'
                },
                createdAt: new Date(),
                updatedAt: new Date(),
                likes: 0,
                likedBy: [],
                views: 0,
                repostCount: 0,
                repostedBy: [],
                category: defaultCategory,
                region: {
                  continent: 'Europe',
                  country: 'United Kingdom',
                  city: 'London'
                },
                language: 'en',
                visibility: 'public' as const,
                mentions: []
              };

              setPosts([newPost, ...posts]);
              setIsCreatePostModalOpen(false);
              showToast('Post created successfully!', 'success');
            } catch (error) {
              showToast('Failed to create post. Please try again.', 'error');
            }
          }}
        />
      </div>
    </>
  );
}